import ko from 'knockout';
import moment, { Moment } from 'moment';
import { customFetch } from '../../fetch';
import { errorToast, successToast } from '../../toastr-helper';
import { resolveCdnUrl } from '../../cdn-url-resolver';
import { filterDeep } from '../../array-utils';
import { openPopup, closePopups } from '../../init';
import numeral from "numeral";
import download from 'downloadjs';

// HACK(Daniele): The typescript compiler would otherwise issue a warning about `Area` not being defined...
declare const _area: string;
export const Area = _area;

class FilterModel {
    id: KnockoutObservable<number>;
    title: KnockoutObservable<string>;
    count: KnockoutObservable<number>;

    constructor(model: any) {
        this.id = ko.observable(model.id);
        this.title = ko.observable(model.title);
        this.count = ko.observable(model.count || 0);
    }
}

// Helper class to keep track of notifications
class NotificationModel {
    notifiedOn: KnockoutObservable<moment.Moment | null>;
    notifiedBy: KnockoutObservable<string>;

    constructor(notifiedOn: any, notifiedBy: string) {
        this.notifiedOn = ko.observable(notifiedOn ? moment(notifiedOn) : null);
        this.notifiedBy = ko.observable(notifiedBy || '');
    }
}

class FileModel {
    id: KnockoutObservable<string>;
    name: KnockoutObservable<string>;
    fileName: KnockoutObservable<string>;

    embedUrl: KnockoutComputed<string>;
    downloadUrl: KnockoutComputed<string>;
    hasFile: KnockoutComputed<boolean>;

    constructor(model: any, embedUrl: string, downloadUrl: string) {
        this.id = ko.observable(model.id);
        this.name = ko.observable(model.name || '');
        this.fileName = ko.observable(model.fileName || '');

        this.embedUrl = ko.pureComputed(() => embedUrl.replace('{id}', this.id()));
        this.downloadUrl = ko.pureComputed(() => downloadUrl.replace('{id}', this.id()));
        this.hasFile = ko.pureComputed(() => !!this.id()); // include null, undefined and the empty string
    }

    public async download() {
        const response = await customFetch(this.downloadUrl());
        if (response.ok) {
            download(await response.blob(), this.fileName());
        }
        else {
            console.error(`${response.errors && response.errors.map(err => err.message).join('\n')}`)
            errorToast('Er ging iets mis met de download');
        }
    }
}

class DocumentModel extends FileModel {
    created: KnockoutObservable<moment.Moment>;
    formattedCreated: KnockoutComputed<string>;

    details: KnockoutComputed<string>;

    constructor(model: any) {
        super(model, `/${Area}/Employee/PersonnelFile/Inline/{id}`, `/${Area}/Employee/PersonnelFile/Download/{id}`);

        this.created = ko.observable(model.created ? moment(model.created) : null);
        this.formattedCreated = ko.pureComputed(() => this.created() ?
            this.created().format('DD-MM-YYYY') : '-');

        this.details = ko.pureComputed(() => this.created() ?
            `${this.name()}, gecreëerd op ${this.formattedCreated()}` : this.name());
    }
}

class ContractModel extends FileModel {
    startDate: KnockoutObservable<moment.Moment>;
    endDate: KnockoutObservable<moment.Moment>;

    formattedStartDate: KnockoutComputed<string>;
    formattedEndDate: KnockoutComputed<string>;

    constructor(model: any) {
        super(model, `/${Area}/Employee/ContractHistory/Inline/{id}`, `/${Area}/Employee/ContractHistory/Download/`);

        this.startDate = ko.observable(moment(model.startDate));
        this.endDate = ko.observable(model.endDate ? moment(model.endDate) : null);

        this.formattedStartDate = ko.pureComputed(() => this.startDate().format('DD-MM-YYYY'));
        this.formattedEndDate = ko.pureComputed(() => this.endDate() ? this.endDate().format('DD-MM-YYYY') : '');
    }
}

export class FilterOptions {
    static RegistrationPending = {
        id: 0,
        title: 'Nog niet geregistreerd',
        notificationUrl: 'RegistrationPending'
    };
    static IDPending = {
        id: 1,
        title: 'Geen legitimatiebewijs geupload',
        notificationUrl: 'IdPending'
    };
    static ContractSignaturePending = {
        id: 2,
        title: 'Contract nog niet ondertekend',
        notificationUrl: 'ContractSignaturePending'
    };
    static DocumentSignaturePending = {
        id: 3,
        title: 'Document nog niet ondertekend',
        notificationUrl: 'DocumentSignaturePending'
    };

    static asArray() {
        return [
            FilterOptions.RegistrationPending,
            FilterOptions.IDPending,
            FilterOptions.ContractSignaturePending,
            FilterOptions.DocumentSignaturePending
        ];
    }
};

export const EmploymentRegistrationSteps = {
    Unknown: 0,
    Profile: 10,
    PersonalData: 11,
    AwaitingIdentification: 20,
    IdentificationDenied: 21,
    IdentificationCheck: 22,
    NoContractSend: 30,
    AwaitingSigning: 31,
    ExpiredSigning: 32,
    ExpiredContract: 33,
    Finished: 100,
    AwaitingSigningContractExtension: 101,
};

export abstract class PopupModel {
    protected callback: any;

    constructor(callback?: any) {
        this.callback = callback;
    }

    public confirm() {
        closePopups();
        this.callback();
    }

    public cancel() {
        closePopups();
    }
}

export class DocumentPreviewModel extends PopupModel {
    selectedDocumentIx: KnockoutObservable<number>;
    employee: KnockoutObservable<FilteredEmployeeModel>;

    documents: KnockoutComputed<DocumentModel[]>;
    selectedDocument: KnockoutComputed<DocumentModel>;

    constructor() {
        super();

        this.selectedDocumentIx = ko.observable(0);
        this.employee = ko.observable<FilteredEmployeeModel>(null);

        this.documents = ko.pureComputed(() => this.employee().pendingDocuments());
        this.selectedDocument = ko.pureComputed(() => this.documents()[this.selectedDocumentIx()]);
    }

    public show(employee: FilteredEmployeeModel) {
        this.employee(employee);
        openPopup('documents-popup');
    }

    public select(index: number) {
        this.selectedDocumentIx(index);
    }
}

export class ContractPreviewModel extends PopupModel {
    employee: KnockoutObservable<FilteredEmployeeModel>;
    contract: KnockoutComputed<ContractModel>;

    constructor() {
        super();

        this.employee = ko.observable<FilteredEmployeeModel>(null);
        this.contract = ko.pureComputed(() => this.employee().pendingContract());
    }

    public show(employee: FilteredEmployeeModel) {
        this.employee(employee);
        openPopup('contract-popup');
    }
}

export class FilteredEmployeeModel {
    readonly documentPreviewModel: DocumentPreviewModel;

    // Keeps track of which filter the employee is linked to
    filterId: KnockoutComputed<number>;

    id: KnockoutObservable<string>;
    name: KnockoutObservable<string>;
    contractStartDate: KnockoutObservable<moment.Moment>;
    contractEndDate: KnockoutObservable<moment.Moment>;
    age: KnockoutObservable<number>;
    pendingContract: KnockoutObservable<ContractModel>;
    contractType: KnockoutObservable<string>;
    hourlyWage: KnockoutObservable<Numeral>;
    pendingDocuments: KnockoutObservable<DocumentModel[]>;
    registrationStatus: KnockoutObservable<number>;
    contractSignaturePending: KnockoutObservable<boolean>;
    documentSignaturePending: KnockoutObservable<boolean>;
    acceptedCompanyPlacement: KnockoutObservable<boolean>;

    allNotifications: KnockoutObservable<NotificationModel[]>;
    notification: KnockoutComputed<NotificationModel>;

    notifiedOn: KnockoutComputed<moment.Moment | null>;
    notifiedBy: KnockoutComputed<string>;
    humanizedNotifiedOn: KnockoutComputed<string>;

    pendingDocumentsDetails: KnockoutComputed<string>;
    branches: KnockoutComputed<string>;
    formattedAvatar: KnockoutComputed<string>;

    // External urls
    detailsUrl: KnockoutComputed<string>;

    constructor(model: any) {
        this.filterId = ko.pureComputed(() => this.getMatchingFilter());

        this.id = ko.observable(model.employeeId);
        this.name = ko.observable(model.employeeName);
        this.contractStartDate = ko.observable(moment(model.contractStartDate));
        this.contractEndDate = ko.observable(model.contractEndDate ? moment(model.contractEndDate) : null);
        this.age = ko.observable(model.age);
        this.pendingContract = ko.observable(model.pendingContract ? new ContractModel(model.pendingContract) : null);
        this.contractType = ko.observable(model.contractType || '-');
        this.hourlyWage = ko.observable(numeral(model.hourlyWage || 0));
        this.registrationStatus = ko.observable(model.registrationStatus);
        this.contractSignaturePending = ko.observable(model.contractSignaturePending);
        this.documentSignaturePending = ko.observable(model.documentSignaturePending);
        this.acceptedCompanyPlacement = ko.observable(model.acceptedCompanyPlacement);

        this.allNotifications = ko.observable(model.allNotifications
            .map((n: any) => new NotificationModel(n.sentOn, n.sentBy)));

        this.notification = ko.pureComputed(() => this.allNotifications()[this.filterId()]);
        this.notifiedOn = ko.pureComputed({
            read: () => this.notification().notifiedOn(),
            write: newValue => this.notification().notifiedOn(newValue)
        });
        this.notifiedBy = ko.pureComputed({
            read: () => this.notification().notifiedBy(),
            write: newValue => this.notification().notifiedBy(newValue)
        });
        this.humanizedNotifiedOn = ko.pureComputed(() => {
            if (this.notifiedOn()) {
                return moment().diff(this.notifiedOn()!, 'days') >= 1 ?
                    this.notifiedOn()!.fromNow() :
                    this.notifiedOn()!.calendar().split(' ')[0];    // calendar is something like 'vandaag...'
            }

            return '-';
        });

        this.pendingDocuments = ko.observable(model.pendingDocuments.map((d: any) => new DocumentModel(d)));
        this.pendingDocumentsDetails = ko.pureComputed(() =>
            `<p style="text-align: left;">
                ${this.pendingDocuments().map(d => d.details()).join('<br />')}
             </p>
             Klik om te zien`);

        this.branches = ko.pureComputed(() => model.branches.join(', '));
        this.formattedAvatar = ko.pureComputed(() =>
            resolveCdnUrl(model.employeeAvatar, {
                thumb: true,
                resize: '30x30^',
                gravity: 'center',
                crop: '30x30+0+50%',
                extent: '30x30',
                optimize: true,
                progressive: true
            }));

        this.detailsUrl = ko.pureComputed(() => `/${Area}/Employee/Detail/${this.id()}`);    // relative url
    }

    protected getMatchingFilter() {
        if (this.registrationStatus() <= EmploymentRegistrationSteps.Profile || !this.acceptedCompanyPlacement()) {
            return FilterOptions.RegistrationPending.id;
        }

        if (this.registrationStatus() === EmploymentRegistrationSteps.IdentificationCheck ||
            this.registrationStatus() === EmploymentRegistrationSteps.IdentificationDenied ||
            this.registrationStatus() === EmploymentRegistrationSteps.AwaitingIdentification) {
            return FilterOptions.IDPending.id;
        }

        if (this.contractSignaturePending()) {
            return FilterOptions.ContractSignaturePending.id;
        }

        if (this.documentSignaturePending()) {
            return FilterOptions.DocumentSignaturePending.id;
        }
        
        return -1; // do not show the employee, since we don't know where s/he belongs
    }

    public formatDate(date: moment.Moment, format = 'DD-MM-YYYY'): string {
        return date ? date.format(format) : '-';
    }

    /**
     * Returns an array representation of the object
     * which is suitable for filtering.
     */
    public asFilterable(): any[] {
        return [
            this.name(),
            this.branches(),
            this.age(),
            this.contractType(),
            this.hourlyWage(),
            this.pendingDocumentsDetails(),
            this.contractStartDate(),
            this.contractEndDate()
        ];
    }

    public async notify(url: string) {
        const notificationUrl = url.replace('{id}', this.id());
        const response = await customFetch(notificationUrl);

        if (response.ok) {
            successToast('Herinnering verstuurd');

            this.notifiedOn(moment(response.data.sentOn));
            this.notifiedBy(response.data.sentBy);
        }
        else {
            console.error(response.errors && response.errors.map(err => err.message).join('\n'));
            errorToast('Er ging wat mis met de herinnering');
        }
    }
}

export abstract class EmployeesStatusModel {
    public readonly baseUrl: string;
    public readonly documentPreview: DocumentPreviewModel;

    loading: KnockoutObservable<boolean>;
    searchFilter: KnockoutObservable<string>;

    filters: KnockoutObservableArray<FilterModel>;
    selectedFilterId: KnockoutObservable<number>;
    selectedFilter: KnockoutComputed<any>;

    notificationUrl: KnockoutComputed<string>;

    /**
     * This is where the list of all employees is stored.
     * Children classes should redefine it.
     *
     * @type {KnockoutObservable<any[]>}
     * @memberof EmployeesStatusModel
     */
    allEmployees: KnockoutObservable<FilteredEmployeeModel[]>;
    visibleEmployees: KnockoutComputed<FilteredEmployeeModel[]>;
    filteredEmployees: KnockoutComputed<FilteredEmployeeModel[][]>;

    showPendingDocuments: KnockoutObservable<boolean>;
    showContractData: KnockoutObservable<boolean>;

    /**
     * The main model for the page. It keeps track of the currently selected
     * section, plus all lists for all sections. The idea is to have a separate
     * list for each section. Every list is populated by filtering the same
     * observable based on the specific filter the list is tied to. Only one list
     * is actually shown at any given time, based on the currently selected filter.
     * Hidden lists are still loaded in order to display the count of their
     * elements and to update this same count if additional filters -- such as
     * free-text search -- are applied.
     *
     * @param loader a loading function returning the data for the page
     */
    constructor() {
        this.baseUrl = `/${Area}/Employees/Status`;

        this.loading = ko.observable(false);
        this.searchFilter = ko.observable('');

        this.filters = ko.observableArray(FilterOptions.asArray().map(option => new FilterModel(option)));
        this.selectedFilterId = ko.observable(FilterOptions.RegistrationPending.id);
        this.selectedFilter = ko.pureComputed(() => FilterOptions.asArray()[this.selectedFilterId()]);

        this.allEmployees = ko.observable([]);
        this.visibleEmployees = ko.pureComputed(() => this.filteredEmployees()[this.selectedFilterId()]);

        // Map each filter to the list of employees matching that filter.
        this.filteredEmployees = ko.pureComputed(() =>
            this.filters()
                .map(filter => this.filterEmployees(this.allEmployees(), filter.id(), this.searchFilter())));

        this.filteredEmployees.subscribe(filteredEmployees => {
            if (!this.loading()) {
                this.updateSections(filteredEmployees);
            }
        });

        this.showPendingDocuments = ko.pureComputed(() => this.selectedFilterId() === FilterOptions.DocumentSignaturePending.id);
        this.showContractData = ko.pureComputed(() => this.selectedFilterId() >= FilterOptions.RegistrationPending.id)

        this.notificationUrl = ko.pureComputed(() => `/${Area}/Employee/Notify/{id}/${this.selectedFilter().notificationUrl}`);
    }

    protected updateSections(filteredEmployees: FilteredEmployeeModel[][]): void {
        // Move the selection to the first non-empty section,
        // unless it's the currently selected one.
        let match = filteredEmployees[this.selectedFilterId()].length > 0;

        this.filters().forEach(filter => {
            const itemsCount = filteredEmployees[filter.id()].length;
            filter.count(itemsCount);

            if (!match && itemsCount > 0) {
                match = true;
                this.selectedFilterId(filter.id());
            }
        });
    }

    /**
     * Filter employees based on the currently selected filter and the search
     * string the user might have input.
     *
     * @param employees
     * @param filterId
     * @param searchFilter
     */
    protected filterEmployees(employees: FilteredEmployeeModel[], filterId: number, searchFilter: string): FilteredEmployeeModel[] {
        const filteredEmployees = employees.filter(e => e.filterId() === filterId);
        return filterDeep(filteredEmployees, searchFilter, e => e.asFilterable());
    }

    protected abstract async getData(...args: any[]): Promise<any[]>;
}

export class PageModel {
    bodyModel: EmployeesStatusModel;
    documentPreviewModel: DocumentPreviewModel;
    contractPreviewModel: ContractPreviewModel;

    constructor(bodyModel: EmployeesStatusModel) {
        this.bodyModel = bodyModel;
        this.documentPreviewModel = new DocumentPreviewModel();
        this.contractPreviewModel = new ContractPreviewModel();
    }

    openDocumentsPreview(employee: FilteredEmployeeModel) {
        this.documentPreviewModel.show(employee);
    }

    openContractPreview(employee: FilteredEmployeeModel) {
        this.contractPreviewModel.show(employee);
    }
}
